import { Button, Grid, Group, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { capitalize, cloneDeep } from 'lodash';
import moment from 'moment';
import { useState } from 'react';

import { EnvironmentCountries, EORIGIN_DATE_FORMAT_WITH_TIME, Transit, TransitStatus } from '@e-origin/shared';

import { Select } from '@components';
import { useReleaseTransit } from '@hooks/transit';
import { useSettings } from '@hooks/useSettings';
import { extractStatus } from '../../utils';
import { ControlNotification } from './ControlNotification';
import { ControlResultDetails } from './ControlResultDetails';
import { CustomsStateNotifications } from './CustomsStateNotifications';
import { DiscrepanciesSection } from './DiscrepanciesSection';
import { getErrorDescription } from './errorDescriptions';
import { InvalidationSection } from './InvalidationSection';
import { NonArrivalInfo } from './NonArrivalInfo';
import { RecoveryNotification } from './RecoveryNotification';

interface TransitViewCustomsProps {
  transit: Transit;
}

export const TransitViewCustoms: React.FC<TransitViewCustomsProps> = ({ transit: transitObj }) => {
  const transit = cloneDeep(transitObj);
  const { data: settings } = useSettings();
  const releaseTransitMutation = useReleaseTransit();
  const [approveRevisions, setApproveRevisions] = useState<boolean | null>(null);
  const showControlNotification =
    transit.customsState?.inControl &&
    (transit.generalInfo.status === TransitStatus.IN_CONTROL ||
      (TransitStatus.ACCEPTED === transit.generalInfo.status && transit.customsState?.flow === 'PRE') ||
      (transit.generalInfo.status === TransitStatus.SENT && transit.customsState?.flow === 'POST'));

  const handleSendReleaseRequest = () => {
    releaseTransitMutation.mutate({ id: transit._id, approveRevisions });
  };

  const updateApproveRevisions = (selectedOption) => {
    setApproveRevisions(selectedOption === 'true');
  };

  if (!transit?.customsState) {
    return (
      <Stack m="md" ml="xl">
        Not sent yet
      </Stack>
    );
  }

  return (
    <ScrollArea h="100%" offsetScrollbars="y">
      <Stack mx="xl">
        <Grid mb="md">
          <Grid.Col span={2}>
            <Stack gap="xs">
              <Text c="gray" size="sm">
                Status
              </Text>
              <Text fw={500}>{extractStatus(transit.generalInfo.status)}</Text>
            </Stack>
          </Grid.Col>
          <Grid.Col span={3}>
            <Stack gap="xs">
              <Text c="gray" size="sm">
                Correlation ID
              </Text>
              <Text fw={500} data-pw="correlation-id-value">
                {transit.customsState?.correlationId ?? 'N/A'}
              </Text>
            </Stack>
          </Grid.Col>
          {transit.TransitOperation?.MRN && (
            <Grid.Col span={3}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Movement reference number
                </Text>
                <Text fw={500}>{transit.TransitOperation.MRN}</Text>
              </Stack>
            </Grid.Col>
          )}
          {transit.customsState?.unloadPermission?.continueUnloading && (
            <Grid.Col span={3}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Received unloading permissions count
                </Text>
                <Text fw={500}>{Number(transit.customsState.unloadPermission.continueUnloading) + 1}</Text>
              </Stack>
            </Grid.Col>
          )}
        </Grid>

        <Grid mb="md">
          {transit.customsState?.dateOfAcceptance && (
            <Grid.Col span={2.5}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Date of acceptance
                </Text>
                <Text fw={500}>{moment(transit.customsState?.dateOfAcceptance).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
              </Stack>
            </Grid.Col>
          )}
          {transit.customsState?.release?.dateOfRelease && (
            <Grid.Col span={2}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Date of release
                </Text>
                <Text fw={500}>{transit.customsState?.release?.dateOfRelease}</Text>
              </Stack>
            </Grid.Col>
          )}
          {transit.customsState?.releaseDestination?.releaseDate && (
            <Grid.Col span={2.5}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Date of release
                </Text>
                <Text fw={500}>{transit.customsState?.releaseDestination?.releaseDate}</Text>
              </Stack>
            </Grid.Col>
          )}
          {transit.customsState?.writeOff?.writeOffDate && (
            <Grid.Col span={2.5}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Date of write-off
                </Text>
                <Text fw={500}>{transit.customsState?.writeOff?.writeOffDate}</Text>
              </Stack>
            </Grid.Col>
          )}
          {transit.customsState?.noRelease?.rejectionDate && (
            <Grid.Col span={2.5}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Rejection date
                </Text>
                <Text fw={500}>{transit.customsState?.noRelease?.rejectionDate}</Text>
              </Stack>
            </Grid.Col>
          )}
        </Grid>

        {transit.customsState?.noRelease && (
          <Grid mb="md">
            {transit.customsState?.noRelease?.noReleaseMotivationCode && (
              <Grid.Col span={3}>
                <Stack gap="xs">
                  <Text c="gray" size="sm">
                    Motivation code
                  </Text>
                  <Text fw={500}>{transit.customsState?.noRelease?.noReleaseMotivationCode}</Text>
                </Stack>
              </Grid.Col>
            )}
            {transit.customsState?.noRelease?.noReleaseMotivationText && (
              <Grid.Col span={4}>
                <Stack gap="xs">
                  <Text c="gray" size="sm">
                    Motivation text
                  </Text>
                  <Text fw={500}>{transit.customsState?.noRelease?.noReleaseMotivationText}</Text>
                </Stack>
              </Grid.Col>
            )}
          </Grid>
        )}

        {transit.customsState?.notifications?.length > 0 && <CustomsStateNotifications transit={transit} />}

        {showControlNotification && <ControlNotification inControl={transit.customsState?.inControl} />}

        <Stack>
          {transit.customsState?.customsDeclarationRejection && (
            <Stack mb="md">
              <Grid>
                <Grid.Col span={12}>
                  <Stack gap="xs">
                    <Text c="gray" size="sm">
                      Business rejection type
                    </Text>
                    <Text fw={500}>{transit.customsState.customsDeclarationRejection.businessRejectionType ?? 'N/A'}</Text>
                  </Stack>
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span={4}>
                  <Stack gap="xs">
                    <Text c="gray" size="sm">
                      Rejection date
                    </Text>
                    <Text fw={500}>{transit.customsState.customsDeclarationRejection.rejectionDate}</Text>
                  </Stack>
                </Grid.Col>
                {transit.customsState.customsDeclarationRejection.rejectionCode && (
                  <Grid.Col span={4}>
                    <Stack gap="xs">
                      <Text c="gray" size="sm">
                        Rejection code
                      </Text>
                      <Text fw={500}>{transit.customsState.customsDeclarationRejection.rejectionCode}</Text>
                    </Stack>
                  </Grid.Col>
                )}
                {transit.customsState.customsDeclarationRejection.rejectionReason && (
                  <Grid.Col span={4}>
                    <Stack gap="xs">
                      <Text c="gray" size="sm">
                        Rejection reason
                      </Text>
                      <Text fw={500}>{transit.customsState.customsDeclarationRejection.rejectionReason}</Text>
                    </Stack>
                  </Grid.Col>
                )}
              </Grid>
            </Stack>
          )}
          {!!transit.customsState?.functionalError?.length && (
            <Stack mt="xs">
              {transit.customsState?.functionalError?.map((item, index) => (
                <Stack key={index} mb="lg">
                  <Title order={5} fw={600}>
                    {capitalize(item.errorType)} error
                  </Title>
                  <Group mb="xs">
                    {Number(item.sequenceNumber) >= 0 && (
                      <Stack>
                        <Text c="gray" size="sm">
                          Sequence number
                        </Text>
                        <Text fw={500}>{item.sequenceNumber}</Text>
                      </Stack>
                    )}
                    <Stack>
                      <Text c="gray" size="sm">
                        Error code
                      </Text>
                      <Text fw={500}>{item.errorCode}</Text>
                    </Stack>
                    <Stack>
                      <Text c="gray" size="sm">
                        Error code description
                      </Text>
                      <Text fw={500}>{getErrorDescription(item.errorType, item.errorCode)}</Text>
                    </Stack>
                  </Group>
                  <Stack mb="xs">
                    <Text c="gray" size="sm">
                      Error pointer
                    </Text>
                    <Text fw={500}>{item.errorPointer}</Text>
                  </Stack>
                  <Stack mb="xs">
                    <Text c="gray" size="sm">
                      Error reason
                    </Text>
                    <Text fw={500}>{item.errorReason}</Text>
                  </Stack>
                  {!!item.remarks?.length && (
                    <Stack mb="xs">
                      <Text c="gray" size="sm">
                        Remarks
                      </Text>
                      <Text fw={500}>{item.remarks}</Text>
                    </Stack>
                  )}
                  {!!item.originalAttributeValue?.length && (
                    <Stack mb="xs">
                      <Text c="gray" size="sm">
                        Original Attribute
                      </Text>
                      <Text fw={500}>{item.originalAttributeValue}</Text>
                    </Stack>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>

        {transit.generalInfo.status === TransitStatus.GUARANTEE_NOT_VALID && (
          <Stack mb="md">
            {!!transit.customsState?.INVGuaranteeReference?.length && (
              <Stack>
                <Title order={5} fw={600}>
                  Guarantee Reference
                </Title>
                {transit.customsState.INVGuaranteeReference.map((guarantee, index) => (
                  <Stack key={index}>
                    <Grid>
                      <Grid.Col span={12}>
                        <Stack gap="xs">
                          <Text c="gray" size="sm">
                            GRN
                          </Text>
                          <Text fw={500}>{guarantee.GRN}</Text>
                        </Stack>
                      </Grid.Col>
                    </Grid>

                    {!!guarantee.InvalidGuaranteeReason?.length &&
                      guarantee.InvalidGuaranteeReason.map((reason, reasonIndex) => (
                        <Grid key={reasonIndex}>
                          {Number(reason.sequenceNumber) > 0 && (
                            <Grid.Col span={2}>
                              <Stack gap="xs">
                                <Text c="gray" size="sm">
                                  Reason number
                                </Text>
                                <Text fw={500}>{reason.sequenceNumber}</Text>
                              </Stack>
                            </Grid.Col>
                          )}
                          <Grid.Col span={2}>
                            <Stack gap="xs">
                              <Text c="gray" size="sm">
                                Reason Code
                              </Text>
                              <Text fw={500}>{reason.code}</Text>
                            </Stack>
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <Stack gap="xs">
                              <Text c="gray" size="sm">
                                Reason Text
                              </Text>
                              <Text fw={500}>{reason.text}</Text>
                            </Stack>
                          </Grid.Col>
                        </Grid>
                      ))}
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        )}

        <InvalidationSection transit={transit} />
        <RecoveryNotification transit={transit} />

        {/** request release */}
        {transit.generalInfo?.status === TransitStatus.IN_CONTROL && settings?.country === EnvironmentCountries.BE && (
          <Stack mb="md" gap="xs">
            <Text c="gray" size="sm">
              Approve revisions
            </Text>
            <Select
              placeholder="Approve revisions"
              data={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ]}
              onChange={updateApproveRevisions}
              maxDropdownHeight={200}
              comboboxProps={{ withinPortal: true }}
              w="47%"
            />
            <Group>
              <Button type="button" disabled={approveRevisions === null} onClick={handleSendReleaseRequest} variant="filled" size="lg">
                Request Release
              </Button>
            </Group>
          </Stack>
        )}
        {/** /request release */}

        {/** requested non-arrival info with dirty state tracking */}
        {transit.customsState?.requestNonArrived?.requestDate && <NonArrivalInfo transit={transit} />}
        {/** / requested non-arrival info */}

        {transit.generalInfo?.status === TransitStatus.NOT_RELEASED && transit.customsState?.controlResult && (
          <ControlResultDetails controlResult={transit.customsState?.controlResult} />
        )}
        {!!transit.customsState?.discrepancies?.length && <DiscrepanciesSection discrepancies={transit.customsState?.discrepancies} />}
      </Stack>
    </ScrollArea>
  );
};
